import {
  Contract,
  ContractTemplate,
  ContractSignature,
  CreateContractRequest,
  UpdateContractRequest,
  ContractSearchCriteria,
  ContractListResponse,
  ContractAnalytics,
  ContractStatus,
  ContractWorkflowStage,
  ContractWorkflowEvent,
  ContractWorkflowEventType,
  ContractCommunication,
  SignatureRequirement,
  ComplianceCheck,
} from '@/types/shared/contracts';
import { CommunityAssessmentData } from '@/types/entities/clients/assessment';
import { CustomProposal } from '@/services/business/sales/proposalGenerationService';

// Export the types for component usage
export type {
  Contract,
  ContractTemplate,
  ContractSignature,
  ContractStatus,
  ContractWorkflowStage,
  ContractType,
  ContractWorkflowEvent,
  ContractCommunication,
  ComplianceCheck,
} from '@/types/shared/contracts';

export class ContractManagementService {
  private readonly STORAGE_KEY = 'contract-management';
  private readonly TEMPLATES_KEY = 'contract-templates';

  // Contract CRUD Operations
  async createContract(request: CreateContractRequest): Promise<Contract> {
    const template = this.getTemplate(request.templateId);
    if (!template) {
      throw new Error(`Contract template not found: ${request.templateId}`);
    }

    const contractId = `contract-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const contractNumber = this.generateContractNumber();

    const inferredTier = this.inferTierFromProposal(request.proposalId);
    const defaultTermMonths = inferredTier === 'custom' ? 60 : 36;
    const startDateIso = new Date().toISOString();
    const endDateIso = new Date(
      new Date(startDateIso).setMonth(new Date(startDateIso).getMonth() + defaultTermMonths),
    ).toISOString();

    const contract: Contract = {
      id: contractId,
      templateId: request.templateId,
      customerId: request.customerId,
      proposalId: request.proposalId,

      contractNumber,
      title: request.title || `${template.name} - ${contractNumber}`,
      description: request.description || template.description,
      type: template.type,
      tier: inferredTier,

      status: 'draft',
      workflowStage: 'creation',
      priority: request.priority || 'medium',

      contractValue: this.calculateContractValue(request.proposalId),
      currency: 'USD',
      paymentTerms: this.getDefaultPaymentTerms(),
      billingSchedule: this.generateBillingSchedule(request.proposalId),

      // Term & Duration defaults
      termMonths: defaultTermMonths,
      minimumTermMonths: defaultTermMonths,
      startDate: startDateIso,
      endDate: endDateIso,
      autoRenewal: true,
      earlyTerminationAllowed: false,
      earlyTerminationPolicy:
        'Early termination is not permitted due to bespoke implementation and compliance costs. Contract auto-renews unless notice is given per renewal clause.',

      content: this.populateTemplate(template, request.fieldValues),
      fieldValues: request.fieldValues,
      attachments: [],

      signatureRequirements: request.customSignatureRequirements?.length
        ? this.processCustomSignatureRequirements(request.customSignatureRequirements)
        : template.signatureRequirements,
      signatures: [],

      createdAt: new Date().toISOString(),
      lastModifiedAt: new Date().toISOString(),

      legalReviewStatus: template.legalReviewRequired ? 'pending' : 'not_required',
      complianceChecks: this.initializeComplianceChecks(template.type),

      workflowHistory: [
        {
          id: `event-${Date.now()}`,
          contractId,
          timestamp: new Date().toISOString(),
          event: 'created',
          toStage: 'creation',
          triggeredBy: 'system', // In production, this would be the current user
          details: 'Contract created from template',
        },
      ],
      communications: [],

      createdBy: 'current-user', // In production, get from auth context
      tags: request.tags || [],
      internalNotes: request.internalNotes,
    };

    this.saveContract(contract);
    this.logWorkflowEvent(contractId, 'created', undefined, 'creation');

    return contract;
  }

  async updateContract(contractId: string, updates: UpdateContractRequest): Promise<Contract> {
    const contract = this.getContract(contractId);
    if (!contract) {
      throw new Error(`Contract not found: ${contractId}`);
    }

    const updatedContract: Contract = {
      ...contract,
      ...updates,
      lastModifiedAt: new Date().toISOString(),
    };

    // Update content if field values changed
    if (updates.fieldValues) {
      const template = this.getTemplate(contract.templateId);
      if (template) {
        updatedContract.content = this.populateTemplate(template, {
          ...contract.fieldValues,
          ...updates.fieldValues,
        });
        updatedContract.fieldValues = {
          ...contract.fieldValues,
          ...updates.fieldValues,
        };
      }
    }

    this.saveContract(updatedContract);
    this.logWorkflowEvent(
      contractId,
      'content_updated',
      contract.workflowStage,
      contract.workflowStage,
    );

    return updatedContract;
  }

  getContract(contractId: string): Contract | null {
    const contracts = this.getAllContracts();
    return contracts.find((c) => c.id === contractId) || null;
  }

  getAllContracts(): Contract[] {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  searchContracts(criteria: ContractSearchCriteria, page = 1, pageSize = 10): ContractListResponse {
    let contracts = this.getAllContracts();

    // Apply filters
    if (criteria.query) {
      const query = criteria.query.toLowerCase();
      contracts = contracts.filter(
        (c) =>
          c.title.toLowerCase().includes(query) ||
          c.description.toLowerCase().includes(query) ||
          c.contractNumber.toLowerCase().includes(query),
      );
    }

    if (criteria.status?.length) {
      contracts = contracts.filter((c) => criteria.status!.includes(c.status));
    }

    if (criteria.type?.length) {
      contracts = contracts.filter((c) => criteria.type!.includes(c.type));
    }

    if (criteria.tier?.length) {
      contracts = contracts.filter((c) => criteria.tier!.includes(c.tier));
    }

    if (criteria.priority?.length) {
      contracts = contracts.filter((c) => criteria.priority!.includes(c.priority));
    }

    if (criteria.customerId) {
      contracts = contracts.filter((c) => c.customerId === criteria.customerId);
    }

    if (criteria.assignedTo) {
      contracts = contracts.filter((c) => c.assignedTo === criteria.assignedTo);
    }

    if (criteria.tags?.length) {
      contracts = contracts.filter((c) => criteria.tags!.some((tag) => c.tags.includes(tag)));
    }

    if (criteria.createdDateRange) {
      const { start, end } = criteria.createdDateRange;
      contracts = contracts.filter((c) => {
        const createdDate = new Date(c.createdAt);
        return createdDate >= new Date(start) && createdDate <= new Date(end);
      });
    }

    if (criteria.contractValueRange) {
      const { min, max } = criteria.contractValueRange;
      contracts = contracts.filter((c) => c.contractValue >= min && c.contractValue <= max);
    }

    // Sort by last modified date (newest first)
    contracts.sort(
      (a, b) => new Date(b.lastModifiedAt).getTime() - new Date(a.lastModifiedAt).getTime(),
    );

    const totalCount = this.getAllContracts().length;
    const filteredCount = contracts.length;

    // Pagination
    const startIndex = (page - 1) * pageSize;
    const paginatedContracts = contracts.slice(startIndex, startIndex + pageSize);

    return {
      contracts: paginatedContracts,
      totalCount,
      filteredCount,
      pagination: {
        page,
        pageSize,
        totalPages: Math.ceil(filteredCount / pageSize),
      },
    };
  }

  // Workflow Management
  async advanceWorkflow(
    contractId: string,
    toStage: ContractWorkflowStage,
    notes?: string,
  ): Promise<Contract> {
    const contract = this.getContract(contractId);
    if (!contract) {
      throw new Error(`Contract not found: ${contractId}`);
    }

    const fromStage = contract.workflowStage;

    // Validate workflow transition
    if (!this.isValidWorkflowTransition(fromStage, toStage)) {
      throw new Error(`Invalid workflow transition from ${fromStage} to ${toStage}`);
    }

    // Update contract
    const updatedContract: Contract = {
      ...contract,
      workflowStage: toStage,
      status: this.mapWorkflowStageToStatus(toStage),
      lastModifiedAt: new Date().toISOString(),
    };

    // Handle stage-specific logic
    if (toStage === 'signature') {
      updatedContract.sentForSignatureAt = new Date().toISOString();
    } else if (toStage === 'execution') {
      updatedContract.fullyExecutedAt = new Date().toISOString();
    }

    this.saveContract(updatedContract);
    this.logWorkflowEvent(contractId, this.mapStageToEvent(toStage), fromStage, toStage, notes);

    return updatedContract;
  }

  // Signature Management
  async addSignature(
    contractId: string,
    signatureData: Omit<ContractSignature, 'id' | 'contractId' | 'signedAt' | 'auditTrail'>,
  ): Promise<Contract> {
    const contract = this.getContract(contractId);
    if (!contract) {
      throw new Error(`Contract not found: ${contractId}`);
    }

    const signature: ContractSignature = {
      id: `signature-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      contractId,
      ...signatureData,
      signedAt: new Date().toISOString(),
      auditTrail: [
        {
          id: `audit-${Date.now()}`,
          timestamp: new Date().toISOString(),
          event: 'signature_created',
          details: `Signature added by ${signatureData.signerName}`,
          ipAddress: signatureData.ipAddress,
          userAgent: signatureData.userAgent,
        },
      ],
    };

    const updatedContract: Contract = {
      ...contract,
      signatures: [...contract.signatures, signature],
      lastModifiedAt: new Date().toISOString(),
    };

    // Check if all required signatures are complete
    const requiredSignatures = contract.signatureRequirements.filter((req) => req.required);
    const completedSignatures = updatedContract.signatures.length;

    if (completedSignatures >= requiredSignatures.length) {
      updatedContract.status = 'fully_executed';
      updatedContract.workflowStage = 'execution';
      updatedContract.fullyExecutedAt = new Date().toISOString();
    } else {
      updatedContract.status = 'partially_signed';
    }

    this.saveContract(updatedContract);
    this.logWorkflowEvent(
      contractId,
      'signed',
      contract.workflowStage,
      updatedContract.workflowStage,
    );

    return updatedContract;
  }

  // Template Management
  getTemplate(templateId: string): ContractTemplate | null {
    const templates = this.getAllTemplates();
    return templates.find((t) => t.id === templateId) || null;
  }

  getAllTemplates(): ContractTemplate[] {
    const stored = localStorage.getItem(this.TEMPLATES_KEY);
    return stored ? JSON.parse(stored) : this.getDefaultTemplates();
  }

  // Analytics and Reporting
  getContractAnalytics(): ContractAnalytics {
    const contracts = this.getAllContracts();

    const contractsByStatus = contracts.reduce(
      (acc, contract) => {
        acc[contract.status] = (acc[contract.status] || 0) + 1;
        return acc;
      },
      {} as Record<ContractStatus, number>,
    );

    const contractsByType = contracts.reduce(
      (acc, contract) => {
        acc[contract.type] = (acc[contract.type] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const executedContracts = contracts.filter((c) => c.fullyExecutedAt);
    const averageTimeToExecution = this.calculateAverageTimeToExecution(executedContracts);

    const totalContractValue = contracts.reduce((sum, c) => sum + c.contractValue, 0);
    const averageContractValue = contracts.length > 0 ? totalContractValue / contracts.length : 0;

    const expiringContracts = this.getExpiringContracts(contracts);
    const recentActivity = this.getRecentActivity(contracts);

    return {
      totalContracts: contracts.length,
      contractsByStatus,
      contractsByType,
      averageTimeToExecution,
      conversionRate: this.calculateConversionRate(),
      totalContractValue,
      averageContractValue,
      expiringContracts,
      recentActivity,
    };
  }

  // Communication Management
  addCommunication(
    contractId: string,
    communication: Omit<ContractCommunication, 'id' | 'contractId' | 'timestamp'>,
  ): void {
    const contract = this.getContract(contractId);
    if (!contract) {
      throw new Error(`Contract not found: ${contractId}`);
    }

    const newCommunication: ContractCommunication = {
      id: `comm-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      contractId,
      timestamp: new Date().toISOString(),
      ...communication,
    };

    const updatedContract: Contract = {
      ...contract,
      communications: [...contract.communications, newCommunication],
      lastModifiedAt: new Date().toISOString(),
    };

    this.saveContract(updatedContract);
  }

  // Integration with Existing Systems
  createContractFromProposal(
    proposalId: string,
    templateType: string = 'service_agreement',
  ): Promise<Contract> {
    // In production, this would fetch the proposal from the proposal service
    const proposalData = this.getProposalData(proposalId);
    if (!proposalData) {
      throw new Error(`Proposal not found: ${proposalId}`);
    }

    const template = this.getTemplateByType(templateType);
    if (!template) {
      throw new Error(`Template not found for type: ${templateType}`);
    }

    const fieldValues = this.mapProposalToContractFields(proposalData);

    return this.createContract({
      templateId: template.id,
      customerId: proposalData.communityName, // Use community name as customer identifier
      proposalId,
      title: `Service Agreement - ${proposalData.communityName}`,
      description: `Service agreement for ${proposalData.communityName} - ${proposalData.tier} tier implementation`,
      fieldValues,
      tags: ['from-proposal', proposalData.tier],
    });
  }

  // Private Helper Methods
  private saveContract(contract: Contract): void {
    const contracts = this.getAllContracts();
    const index = contracts.findIndex((c) => c.id === contract.id);

    if (index >= 0) {
      contracts[index] = contract;
    } else {
      contracts.push(contract);
    }

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(contracts));
  }

  private logWorkflowEvent(
    contractId: string,
    event: ContractWorkflowEventType,
    fromStage?: ContractWorkflowStage,
    toStage?: ContractWorkflowStage,
    details?: string,
  ): void {
    const contract = this.getContract(contractId);
    if (!contract) return;

    const workflowEvent: ContractWorkflowEvent = {
      id: `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      contractId,
      timestamp: new Date().toISOString(),
      event,
      fromStage,
      toStage,
      triggeredBy: 'current-user', // In production, get from auth context
      details,
    };

    const updatedContract: Contract = {
      ...contract,
      workflowHistory: [...contract.workflowHistory, workflowEvent],
    };

    this.saveContract(updatedContract);
  }

  private generateContractNumber(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const sequence = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, '0');

    return `CTR-${year}${month}${day}-${sequence}`;
  }

  private populateTemplate(
    template: ContractTemplate,
    fieldValues: Record<string, string | number | boolean>,
  ): string {
    let content = template.templateContent;

    // Replace placeholders with actual values
    Object.entries(fieldValues).forEach(([key, value]) => {
      const placeholder = `{{${key}}}`;
      content = content.replace(new RegExp(placeholder, 'g'), String(value));
    });

    return content;
  }

  private inferTierFromProposal(proposalId?: string): 'silver' | 'gold' | 'custom' {
    if (!proposalId) return 'silver';

    const proposalData = this.getProposalData(proposalId);
    return proposalData?.tier || 'silver';
  }

  private calculateContractValue(proposalId?: string): number {
    if (!proposalId) return 0;

    const proposalData = this.getProposalData(proposalId);
    return proposalData?.investmentDetails?.totalFirstYear || 0;
  }

  private getDefaultPaymentTerms() {
    return {
      paymentMethod: 'credit_card' as const,
      netDays: 30,
      currency: 'USD',
    };
  }

  private generateBillingSchedule(proposalId?: string) {
    return {
      type: 'recurring' as const,
      frequency: 'monthly' as const,
      startDate: new Date().toISOString(),
    };
  }

  private initializeComplianceChecks(contractType: string): ComplianceCheck[] {
    const checks: ComplianceCheck[] = [
      {
        id: `check-${Date.now()}-1`,
        type: 'internal_approval',
        status: 'pending',
      },
    ];

    if (contractType === 'service_agreement') {
      checks.push({
        id: `check-${Date.now()}-2`,
        type: 'legal_review',
        status: 'pending',
      });
    }

    return checks;
  }

  private isValidWorkflowTransition(
    from: ContractWorkflowStage,
    to: ContractWorkflowStage,
  ): boolean {
    const validTransitions: Record<ContractWorkflowStage, ContractWorkflowStage[]> = {
      creation: ['content_review', 'legal_review'],
      content_review: ['creation', 'legal_review', 'approval'],
      legal_review: ['content_review', 'approval'],
      approval: ['content_review', 'customer_review'],
      customer_review: ['negotiation', 'signature'],
      negotiation: ['customer_review', 'content_review'],
      signature: ['execution'],
      execution: ['fulfillment'],
      fulfillment: [],
    };

    return validTransitions[from]?.includes(to) || false;
  }

  private mapWorkflowStageToStatus(stage: ContractWorkflowStage): ContractStatus {
    const mapping: Record<ContractWorkflowStage, ContractStatus> = {
      creation: 'draft',
      content_review: 'under_review',
      legal_review: 'under_review',
      approval: 'pending_approval',
      customer_review: 'ready_for_signature',
      negotiation: 'under_review',
      signature: 'ready_for_signature',
      execution: 'fully_executed',
      fulfillment: 'fully_executed',
    };

    return mapping[stage] || 'draft';
  }

  private mapStageToEvent(stage: ContractWorkflowStage): ContractWorkflowEventType {
    const mapping: Record<ContractWorkflowStage, ContractWorkflowEventType> = {
      creation: 'created',
      content_review: 'submitted_for_review',
      legal_review: 'submitted_for_review',
      approval: 'approved',
      customer_review: 'sent_to_customer',
      negotiation: 'negotiation_requested',
      signature: 'sent_for_signature',
      execution: 'fully_executed',
      fulfillment: 'fully_executed',
    };

    return mapping[stage] || 'content_updated';
  }

  private calculateAverageTimeToExecution(executedContracts: Contract[]): number {
    if (executedContracts.length === 0) return 0;

    const totalDays = executedContracts.reduce((sum, contract) => {
      if (!contract.fullyExecutedAt) return sum;

      const created = new Date(contract.createdAt);
      const executed = new Date(contract.fullyExecutedAt);
      const days = Math.ceil((executed.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));

      return sum + days;
    }, 0);

    return Math.round(totalDays / executedContracts.length);
  }

  private calculateConversionRate(): number {
    // This would integrate with the proposal service to calculate
    // what percentage of proposals become executed contracts
    return 0; // Placeholder
  }

  private getExpiringContracts(contracts: Contract[]): Contract[] {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    return contracts.filter((contract) => {
      if (!contract.expirationDate) return false;
      const expirationDate = new Date(contract.expirationDate);
      return expirationDate <= thirtyDaysFromNow && contract.status !== 'fully_executed';
    });
  }

  private getRecentActivity(contracts: Contract[]): ContractWorkflowEvent[] {
    const allEvents = contracts.flatMap((contract) => contract.workflowHistory);

    return allEvents
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 10);
  }

  private getDefaultTemplates(): ContractTemplate[] {
    // Return default contract templates
    return []; // Placeholder - would be populated with actual templates
  }

  private getTemplateByType(type: string): ContractTemplate | null {
    const templates = this.getAllTemplates();
    return templates.find((t) => t.type === type) || null;
  }

  private getProposalData(proposalId: string): CustomProposal | null {
    // In production, this would integrate with the proposal service
    return null; // Placeholder
  }

  private mapProposalToContractFields(
    proposalData: CustomProposal,
  ): Record<string, string | number | boolean> {
    // Map proposal data to contract field values
    return {}; // Placeholder
  }

  private processCustomSignatureRequirements(
    requirements: Partial<SignatureRequirement>[],
  ): SignatureRequirement[] {
    return requirements.map((req, index) => ({
      id: `sig-req-${Date.now()}-${index}`,
      role: req.role || 'customer',
      required: req.required !== false,
      order: req.order || index + 1,
      signatureType: req.signatureType || 'electronic',
      ...req,
    })) as SignatureRequirement[];
  }
}

export const contractManagementService = new ContractManagementService();
